import { useState, useEffect, useCallback, useMemo } from 'react';
import { StaffRole } from '@mygate/common';
import { useAuthStore } from '@/stores/authStore';
import { staffAuthService } from '@/services/staffAuth';
import { ApiError } from '@/services/api';

export function useAuth() {
  const [isInitializing, setIsInitializing] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get all state from store
  const {
    isAuthenticated,
    staff,
    phoneNumber,
    accessToken,
    refreshToken,
    setPhoneNumber,
    setStaff,
    setTokens,
    logout: logoutStore,
  } = useAuthStore();

  // Initialize auth on mount
  useEffect(() => {
    let isMounted = true;

    const initialize = async () => {
      try {
        // If we have tokens, try to fetch profile
        if (accessToken || refreshToken) {
          try {
            const response = await staffAuthService.getProfile();
            if (response.data && isMounted) {
              setStaff({
                id: response.data.id,
                firstName: response.data.firstName,
                lastName: response.data.lastName,
                role: response.data.role,
              });
            }
          } catch (err) {
            // If profile fetch fails with 401, user needs to re-login
            if (err instanceof ApiError && err.status === 401 && isMounted) {
              logoutStore();
            }
            // Silent fail for other errors during initialization
          }
        }
      } catch (error) {
        // Silent fail for unexpected errors during initialization
      } finally {
        // Always set initialization to false
        if (isMounted) {
          setIsInitializing(false);
        }
      }
    };

    initialize();

    return () => {
      isMounted = false;
    };
  }, [accessToken, refreshToken, setStaff, logoutStore]);

  // Request OTP
  const requestOtp = useCallback(
    async (phone: string) => {
      setIsLoading(true);
      setError(null);
      try {
        const formattedPhone = phone.startsWith('+') ? phone : `+${phone}`;
        await staffAuthService.requestOtp(formattedPhone);
        setPhoneNumber(formattedPhone);
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to send OTP';
        setError(message);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [setPhoneNumber],
  );

  // Verify OTP
  const verifyOtp = useCallback(
    async (code: string) => {
      if (!phoneNumber) {
        setError('Phone number not found');
        return false;
      }

      setIsLoading(true);
      setError(null);
      try {
        const response = await staffAuthService.verifyOtp(phoneNumber, code);
        if (response.data) {
          setStaff(response.data.staff);
          setTokens(response.data.accessToken, response.data.refreshToken);
          return true;
        }
        return false;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Invalid OTP';
        setError(message);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [phoneNumber, setStaff, setTokens],
  );

  // Logout
  const logout = useCallback(async () => {
    setIsLoading(true);
    try {
      await staffAuthService.logout();
    } catch {
      // Logout anyway even if API call fails
    } finally {
      logoutStore();
      setIsLoading(false);
    }
  }, [logoutStore]);

  // Refresh profile
  const refreshProfile = useCallback(async () => {
    try {
      const response = await staffAuthService.getProfile();
      if (response.data) {
        setStaff({
          id: response.data.id,
          firstName: response.data.firstName,
          lastName: response.data.lastName,
          role: response.data.role,
        });
        return { success: true, data: response.data, error: null };
      }
      return { success: false, data: null, error: 'No profile data received' };
    } catch (err) {
      console.error('Failed to refresh profile:', err);

      // If profile fetch fails with 401, user needs to re-login
      if (err instanceof ApiError && err.status === 401) {
        logoutStore();
        return { success: false, data: null, error: 'Session expired' };
      }

      const errorMessage = err instanceof Error ? err.message : 'Failed to refresh profile';
      return { success: false, data: null, error: errorMessage };
    }
  }, [setStaff, logoutStore]);

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Memoized computed properties
  const isAdmin = useMemo(() => staff?.role === StaffRole.ADMIN, [staff?.role]);
  const isSupport = useMemo(() => staff?.role === StaffRole.SUPPORT, [staff?.role]);

  return {
    // State
    isInitializing,
    isLoading,
    isAuthenticated,
    staff,
    phoneNumber,
    error,

    // Computed
    isAdmin,
    isSupport,

    // Actions
    requestOtp,
    verifyOtp,
    logout,
    refreshProfile,
    setPhoneNumber,
    clearError,
  };
}
